import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AUTH_CONFIG, type AuthConfig } from '@olv/ui-shared/features/auth';
import {
  Account,
  CreateAccountDto,
  UpdateAccountDto,
  ActivityLogEntry,
  AccountFilters,
  PagedResult,
  AccountStatus,
  ActivityLogItem,
  CreateAccountResponse,
} from '../models/account.interface';
import { UserDto, userDtoToAccount } from '../models/user.model';
import { PagedResultDto } from '../models/paged-result.dto';

export enum ActivityTypeEnum {
  // Authentication
  Login = 0,
  Logout = 1,
  FailedPasswordLogin = 2,
  Failed2FALogin = 3,

  // Account Management
  CreateAccount = 100,
  EnableAccount = 101,
  DisableAccount = 102,
  ResetPassword = 103,
  Reset2FA = 104,
  ResetAccount = 105,
  EditAccount = 106,

  // Merchant Management
  CreateMerchant = 200,
  EnableMerchant = 201,
  DisableMerchant = 202,
  EditMerchant = 203,

  // Wallet Management
  CreateSystemWallet = 300,
  EditSystemWallet = 301,
  CreateMerchantWallet = 302,
  EditMerchantWallet = 303,

  // Whitelisted Wallet Management
  CreateSystemWhitelistedWallet = 400,
  ApproveSystemWhitelistedWallet = 401,
  RejectSystemWhitelistedWallet = 402,
  RevokeSystemWhitelistedWallet = 403,
  CreateMerchantWhitelistedWallet = 404,
  ApproveMerchantWhitelistedWallet = 405,
  RejectMerchantWhitelistedWallet = 406,
  RevokeMerchantWhitelistedWallet = 407,

  // Transaction Management
  PrepareSystemTransaction = 500,
  ApproveSystemTransaction = 501,
  RejectSystemTransaction = 502,
  PrepareMerchantTransaction = 503,
  ApproveMerchantTransaction = 504,
  RejectMerchantTransaction = 505,

  // Report Management
  ExportTransactionsReport = 600,
  ExportWalletsReport = 601,
  ExportUserActivityReport = 602,
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private http = inject(HttpClient);

  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);
  private readonly apiUrl = `${this.cfg.baseUrl}/api/Account`;
  // Signal-based state management
  private accountsSignal = signal<Account[]>([]);
  private selectedAccountSignal = signal<Account | null>(null);
  private accountProfileSignal = signal<Account | null>(null);
  private loadingSignal = signal<boolean>(false);
  private filtersSignal = signal<AccountFilters>({});
  private totalCountSignal = signal<number>(0);
  private updateCounter = signal(0);
  private accountGroupsSignal = signal<{ id: string; name: string }[]>([]);
  // Computed signals
  readonly accounts = this.accountsSignal.asReadonly();
  readonly selectedAccount = this.selectedAccountSignal.asReadonly();
  readonly accountProfile = this.accountProfileSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly filters = this.filtersSignal.asReadonly();
  readonly totalCount = this.totalCountSignal.asReadonly();
  readonly profileUpdated = computed(() => this.updateCounter());

  readonly filteredAccounts = computed(() => this.accountsSignal());

  getAccounts(filters?: AccountFilters, pageIndex = 0): Observable<PagedResult<Account>> {
    this.loadingSignal.set(true);

    let params = new HttpParams();

    // Backend-supported filters
    if (filters?.search) {
      params = params.set('Filter', filters.search);
    }

    if (filters?.status) {
      const isActive = filters.status === AccountStatus.ACTIVE;
      params = params.set('IsActive', isActive.toString());
    }
    if (filters?.role !== undefined) {
      params = params.set('Role', filters.role.toString());
    }

    // Sorting
    if (filters?.sortBy) {
      let sorting = '';
      switch (filters.sortBy) {
        case 'name_asc':
          sorting = 'name ASC';
          break;
        case 'name_desc':
          sorting = 'name DESC';
          break;
      }
      params = params.set('Sorting', sorting);
    }

    params = params.set('SkipCount', pageIndex.toString());

    return this.http.get<PagedResultDto<UserDto>>(this.apiUrl, { params }).pipe(
      map((response) => {
        const accounts = response.items.map(userDtoToAccount);
        return {
          items: accounts,
          totalCount: response.totalCount,
          pageIndex,
          totalPages: Math.ceil(response.totalCount),
        };
      }),
      tap((result) => {
        this.accountsSignal.set(result.items);
        this.totalCountSignal.set(result.totalCount || 0);
        this.loadingSignal.set(false);
      }),
      catchError((error) => {
        this.loadingSignal.set(false);
        console.error('Error loading accounts:', error);
        return throwError(() => new Error('Failed to load accounts'));
      }),
    );
  }

  getAccount(id: string): Observable<Account> {
    return this.http.get<UserDto>(`${this.apiUrl}/${id}`).pipe(
      map((user) => userDtoToAccount(user)),
      tap((account) => this.selectedAccountSignal.set(account)),
      catchError((error) => {
        console.error('Error loading account:', error);
        return throwError(() => new Error(`Failed to load account: ${error.message}`));
      }),
    );
  }

  getAccountByUsername(username: string): Observable<Account> {
    return this.http.get<UserDto>(`${this.apiUrl}/username/${username}`).pipe(
      map((user) => userDtoToAccount(user)),
      tap((account) => this.selectedAccountSignal.set(account)),
      catchError((error) => {
        console.error('Error loading account by username:', error);
        return throwError(() => new Error(`Failed to load account: ${error.message}`));
      }),
    );
  }

  getAccountProfile(): Observable<Account> {
    return this.http.get<UserDto>(`${this.apiUrl}/profile`).pipe(
      map((user) => userDtoToAccount(user)),
      tap((account) => {
        this.accountProfileSignal.set(account);
        this.selectedAccountSignal.set(account);
      }),
      catchError((error) => {
        console.error('Error loading account by username:', error);
        return throwError(() => new Error(`Failed to load account: ${error.message}`));
      }),
    );
  }

  createAccount(accountData: CreateAccountDto): Observable<CreateAccountResponse> {
    this.loadingSignal.set(true);

    const formData = new FormData();

    formData.append('FirstName', accountData.firstName);
    formData.append('LastName', accountData.lastName);
    formData.append('CompanyPosition', accountData.position);
    formData.append('Email', accountData.email);

    // Append group IDs
    if (Array.isArray(accountData.groups)) {
      accountData.groups.forEach((groupId) => {
        formData.append('Groups', groupId);
      });
    }

    // Append profile file if available
    if (accountData.profileFile instanceof File) {
      formData.append('ProfileFile', accountData.profileFile);
    }

    return this.http.post<CreateAccountResponse>(this.apiUrl, formData).pipe(
      catchError((error) => {
        const serverMessage = error.error || error.message || 'Unknown error';
        return throwError(() => new Error(serverMessage));
      }),
    );
  }

  updateAccount(id: string, accountData: UpdateAccountDto): Observable<Account> {
    const formData = new FormData();

    if (accountData.firstName != null) {
      formData.append('FirstName', accountData.firstName);
    }
    if (accountData.lastName != null) {
      formData.append('LastName', accountData.lastName);
    }
    if (accountData.position != null) {
      formData.append('CompanyPosition', accountData.position);
    }
    if (accountData.groups != null) {
      accountData.groups.forEach((groupId) => {
        formData.append('Groups', groupId);
      });
    }
    if (accountData.profileFile != null) {
      formData.append('ProfileFile', accountData.profileFile);
    }

    return this.http.put<UserDto>(`${this.apiUrl}/${id}`, formData).pipe(
      map(userDtoToAccount),
      tap(() => {
        this.updateCounter.update((n) => n + 1);
      }),
      catchError((error) => {
        console.error('Error updating account:', error);
        return throwError(() => new Error(`Failed to update account: ${error.message}`));
      }),
    );
  }

  deactivateAccount(id: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/disable`, {}).pipe(
      tap(() => {
        // Update local state
        const currentAccounts = this.accountsSignal();
        const accountIndex = currentAccounts.findIndex((acc) => acc.id === id);
        if (accountIndex >= 0) {
          const updatedAccounts = [...currentAccounts];
          updatedAccounts[accountIndex] = {
            ...updatedAccounts[accountIndex],
            status: AccountStatus.INACTIVE,
          };
          this.accountsSignal.set(updatedAccounts);
        }
      }),
      catchError((error) => {
        console.error('Error deactivating account:', error);
        return throwError(() => new Error(`Failed to deactivate account: ${error.message}`));
      }),
    );
  }

  activateAccount(id: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/enable`, {}).pipe(
      tap(() => {
        // Update local state
        const currentAccounts = this.accountsSignal();
        const accountIndex = currentAccounts.findIndex((acc) => acc.id === id);
        if (accountIndex >= 0) {
          const updatedAccounts = [...currentAccounts];
          updatedAccounts[accountIndex] = {
            ...updatedAccounts[accountIndex],
            status: AccountStatus.ACTIVE,
          };
          this.accountsSignal.set(updatedAccounts);
        }
      }),
      catchError((error) => {
        console.error('Error activating account:', error);
        return throwError(() => new Error(`Failed to activate account: ${error.message}`));
      }),
    );
  }

  deleteAccount(id: string): Observable<void> {
    // Note: Backend doesn't have a delete endpoint, only disable
    return this.deactivateAccount(id);
  }

  getActivityLog(
    accountId: string,
    activityType?: ActivityTypeEnum,
    startDateTime?: string,
    endDateTime?: string,
    skipCount?: number,
    maxResultCount?: number,
    merchantId?: string,
    isMerchant?: boolean,
  ): Observable<ActivityLogEntry> {
    let url: string;

    if (isMerchant && merchantId) {
      url = `${this.cfg.baseUrl}/api/merchantaccount/${merchantId}/users/${accountId}/activity-logs`;
    } else {
      url = `${this.apiUrl}/${accountId}/activity-logs`;
    }
    const queryParams: string[] = [];

    if (skipCount !== undefined) {
      queryParams.push(`SkipCount=${skipCount}`);
    }

    if (maxResultCount !== undefined) {
      queryParams.push(`MaxResultCount=${maxResultCount}`);
    }

    if (activityType !== undefined && activityType !== null) {
      queryParams.push(`activityType=${activityType}`);
    }

    if (startDateTime) {
      queryParams.push(`StartDateTime=${encodeURIComponent(startDateTime)}`);
    }

    if (endDateTime) {
      queryParams.push(`EndDateTime=${encodeURIComponent(endDateTime)}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return this.http.get<any>(url).pipe(
      map((response) => {
        const items: ActivityLogItem[] =
          response.items?.map((log: any) => ({
            id: log.id,
            activityType: log.activityType,
            entityNameValue: log.entityNameValue,
            date: new Date(log.date),
          })) || [];

        return {
          items,
          totalCount: response.totalCount || items.length,
        };
      }),
      catchError((error) => {
        console.error('Error loading activity log:', error);
        return of({ items: [], totalCount: 0 });
      }),
    );
  }

  // State management methods
  setFilters(filters: AccountFilters): void {
    this.filtersSignal.set({ ...this.filtersSignal(), ...filters });
  }

  clearFilters(): void {
    this.filtersSignal.set({});
  }

  selectAccount(account: Account | null): void {
    this.selectedAccountSignal.set(account);
  }

  // Security actions - Integrated with backend
  resetPassword(accountId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${accountId}/reset-password`, {}).pipe(
      tap(() => {
        console.log(`Account reset initiated for user ${accountId}`);
      }),
      catchError((error) => {
        console.error('Error resetting account:', error);
        return throwError(() => new Error(`Failed to reset account: ${error.message}`));
      }),
    );
  }

  reset2FA(accountId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${accountId}/reset-2fa`, {}).pipe(
      tap(() => {
        console.log(`Account reset initiated for user ${accountId}`);
      }),
      catchError((error) => {
        console.error('Error resetting account:', error);
        return throwError(() => new Error(`Failed to reset account: ${error.message}`));
      }),
    );
  }

  resetAccount(accountId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${accountId}/reset-account`, {}).pipe(
      tap(() => {
        console.log(`Account reset initiated for user ${accountId}`);
      }),
      catchError((error) => {
        console.error('Error resetting account:', error);
        return throwError(() => new Error(`Failed to reset account: ${error.message}`));
      }),
    );
  }

  // Call to get all account groups
  getAccountGroups(): Observable<{ id: string; name: string }[]> {
    const cached = this.accountGroupsSignal();
    if (cached.length > 0) {
      return of(cached);
    }
    return this.http.get<{ id: string; name: string }[]>(`${this.apiUrl}/groups`).pipe(
      tap((groups) => this.accountGroupsSignal.set(groups)),
      catchError((error) => {
        console.error('Error fetching account groups:', error);
        return throwError(() => new Error('Failed to load account groups'));
      }),
    );
  }
}
